// store/dentalAI.js
// Enhanced dental AI store module with real API integration

import axios from "axios";
import { eventBus } from "@/main";
import DentalAIService from "@/services/DentalAIService";
import TreatmentMapping from "@/utils/treatmentMapping";

// Helper function to convert API response to legacy format
const convertToLegacyFormat = (detections) => {
  const problems = [];

  Object.keys(detections).forEach(category => {
    const categoryDetections = detections[category] || [];

    categoryDetections.forEach((detection, index) => {
      problems.push({
        id: detection.id || `${category}_${index}`,
        toothNo: null,  // AI doesn't provide tooth number
        surfaces: [],    // AI doesn't provide surfaces
        condition: detection.class_name,
        category: category,
        confidence: detection.confidence,
        severity: getSeverityFromConfidence(detection.confidence),
        boundingBox: detection.box,
        photoType: detection.photoType
      });
    });
  });

  return problems;
};

// Helper function to get severity from confidence
const getSeverityFromConfidence = (confidence) => {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'moderate';
  if (confidence >= 0.4) return 'mild';
  return 'uncertain';
};

export default {
  namespaced: true,

  state: {
    // Photo capture state
    upperOcclusalPhoto: null,
    lowerOcclusalPhoto: null,
    capturedPhotos: [],
    
    // AI analysis state
    analysisInProgress: false,
    analysisResults: null,
    analysisError: null,
    analysisId: null,
    analysisTimestamp: null,
    
    // Real AI detection results
    detectedProblems: [],
    detectionSummary: null,
    detectionCategories: {},
    
    // Treatment suggestions
    suggestedTreatments: [],
    autoGeneratedPlan: [],
    treatmentMappings: {},
    
    // Workflow state
    currentStep: 1,
    workflowCompleted: false,
    
    // API state
    apiConnected: false,
    lastApiCall: null
  },

  mutations: {
    SET_UPPER_OCCLUSAL_PHOTO(state, photo) {
      state.upperOcclusalPhoto = photo;
    },
    
    SET_LOWER_OCCLUSAL_PHOTO(state, photo) {
      state.lowerOcclusalPhoto = photo;
    },
    
    ADD_CAPTURED_PHOTO(state, photo) {
      state.capturedPhotos.push(photo);
    },
    
    CLEAR_CAPTURED_PHOTOS(state) {
      state.capturedPhotos = [];
      state.upperOcclusalPhoto = null;
      state.lowerOcclusalPhoto = null;
    },
    
    SET_ANALYSIS_IN_PROGRESS(state, inProgress) {
      state.analysisInProgress = inProgress;
    },
    
    SET_ANALYSIS_RESULTS(state, results) {
      state.analysisResults = results;
      state.analysisError = null;
    },
    
    SET_ANALYSIS_ERROR(state, error) {
      state.analysisError = error;
      state.analysisResults = null;
    },
    
    SET_SUGGESTED_TREATMENTS(state, treatments) {
      state.suggestedTreatments = treatments;
    },
    
    SET_AUTO_GENERATED_PLAN(state, plan) {
      state.autoGeneratedPlan = plan;
    },
    
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step;
    },
    
    SET_WORKFLOW_COMPLETED(state, completed) {
      state.workflowCompleted = completed;
    },
    
    // Enhanced AI mutations
    SET_ANALYSIS_ID(state, id) {
      state.analysisId = id;
    },
    
    SET_ANALYSIS_TIMESTAMP(state, timestamp) {
      state.analysisTimestamp = timestamp;
    },
    
    SET_DETECTED_PROBLEMS(state, problems) {
      state.detectedProblems = problems;
    },
    
    SET_DETECTION_SUMMARY(state, summary) {
      state.detectionSummary = summary;
    },
    
    SET_DETECTION_CATEGORIES(state, categories) {
      state.detectionCategories = categories;
    },
    
    SET_TREATMENT_MAPPINGS(state, mappings) {
      state.treatmentMappings = mappings;
    },
    
    SET_API_CONNECTED(state, connected) {
      state.apiConnected = connected;
    },
    
    SET_LAST_API_CALL(state, timestamp) {
      state.lastApiCall = timestamp;
    },
    
    RESET_WORKFLOW(state) {
      state.currentStep = 1;
      state.workflowCompleted = false;
      state.capturedPhotos = [];
      state.upperOcclusalPhoto = null;
      state.lowerOcclusalPhoto = null;
      state.analysisResults = null;
      state.analysisError = null;
      state.analysisId = null;
      state.analysisTimestamp = null;
      state.detectedProblems = [];
      state.detectionSummary = null;
      state.detectionCategories = {};
      state.suggestedTreatments = [];
      state.autoGeneratedPlan = [];
      state.treatmentMappings = {};
      state.apiConnected = false;
    }
  },

  actions: {
    // Save captured photo
    saveCapturedPhoto({ commit }, { type, photoData }) {
      return new Promise((resolve) => {
        if (type === 'upper') {
          commit('SET_UPPER_OCCLUSAL_PHOTO', photoData);
        } else if (type === 'lower') {
          commit('SET_LOWER_OCCLUSAL_PHOTO', photoData);
        }
        commit('ADD_CAPTURED_PHOTO', { type, data: photoData, timestamp: new Date() });
        resolve();
      });
    },
    
    // Real AI photo analysis using DentalAIService
    async analyzeDentalPhotos({ commit, state, dispatch, rootState }, payload) {
      commit('SET_ANALYSIS_IN_PROGRESS', true);
      commit('SET_ANALYSIS_ERROR', null);
      commit('SET_LAST_API_CALL', new Date().toISOString());

      try {
        // Get photos from protocolMedia system or fallback to old system
        let upperPhoto = payload.photoUrls?.upper;
        let lowerPhoto = payload.photoUrls?.lower;

        // If no URLs provided, get from protocolMedia store
        if (!upperPhoto && rootState.protocolMedia?.localPhotos?.upper?.base64) {
          upperPhoto = rootState.protocolMedia.localPhotos.upper.base64;
        }
        if (!lowerPhoto && rootState.protocolMedia?.localPhotos?.lower?.base64) {
          lowerPhoto = rootState.protocolMedia.localPhotos.lower.base64;
        }

        // Fallback to old system photos
        if (!upperPhoto) upperPhoto = state.upperOcclusalPhoto;
        if (!lowerPhoto) lowerPhoto = state.lowerOcclusalPhoto;

        // Validate that we have both photos
        if (!upperPhoto || !lowerPhoto) {
          throw new Error('Missing required photos for analysis');
        }
        
        // Call real AI API
        const analysisResponse = await DentalAIService.analyzePhotos({
          upperPhoto: upperPhoto,
          lowerPhoto: lowerPhoto,
          threshold: payload.threshold || 0.5,
          patientId: payload.patientId
        });
        
        if (!analysisResponse.success) {
          // Handle API failure - no fallback
          console.warn('AI API failed:', analysisResponse.error);

          commit('SET_ANALYSIS_ERROR', analysisResponse.error);
          commit('SET_API_CONNECTED', false);
          commit('SET_ANALYSIS_IN_PROGRESS', false);

          // Don't create any treatment plan if AI fails
          throw new Error(analysisResponse.error?.message || 'AI analysis failed');
        }
        
        // Process successful AI analysis
        commit('SET_API_CONNECTED', true);
        commit('SET_ANALYSIS_ID', analysisResponse.analysisId);
        commit('SET_ANALYSIS_TIMESTAMP', analysisResponse.timestamp);
        commit('SET_ANALYSIS_RESULTS', analysisResponse);
        
        // Store detailed detection results
        commit('SET_DETECTED_PROBLEMS', analysisResponse.results.detections);
        commit('SET_DETECTION_SUMMARY', analysisResponse.summary);
        commit('SET_DETECTION_CATEGORIES', analysisResponse.results.detections);
        
        // Generate treatment plan from AI detections
        await dispatch('processAnalysisResults', {
          results: analysisResponse.results,
          summary: analysisResponse.summary,
          ...payload
        });
        
        return analysisResponse;
        
      } catch (error) {
        console.error('Dental AI analysis failed:', error);
        
        const errorDetails = {
          message: error.message || 'AI analysis failed',
          timestamp: new Date().toISOString(),
          details: error.details || null
        };
        
        commit('SET_ANALYSIS_ERROR', errorDetails);
        commit('SET_API_CONNECTED', false);
        commit('SET_ANALYSIS_IN_PROGRESS', false);

        // No fallback - just throw the error
        throw error;
      } finally {
        commit('SET_ANALYSIS_IN_PROGRESS', false);
      }
    },
    
    // Process AI analysis results and generate treatment plan using database
    async processAnalysisResults({ commit, dispatch, rootState }, { results, summary, ...payload }) {
      try {
        
        // Pass the entire Vuex store context to TreatmentMapping for database access
        const storeContext = {
          getters: {
            'moduleAppointment/getTreatDiagList': rootState.moduleAppointment?.treatDiagList || [],
            'moduleAppointment/getTreatList': rootState.moduleAppointment?.treatList || [],
            'moduleAppointment/getNhsoMtreatIds': (() => {
              const treatList = rootState.moduleAppointment?.treatList || [];
              return treatList.filter(i => i.branchId === 0).map(i => i.id);
            })(),
            'moduleAppointment/getTreatDiagBy': (id, key) => {
              const diagList = rootState.moduleAppointment?.treatDiagList || [];
              return diagList.find(item => item[key] === id) || {};
            }
          }
        };
        
        // Generate treatment plan using database-driven TreatmentMapping
        const treatmentPlan = await TreatmentMapping.generateTreatmentPlan(
          results.detections, 
          {
            includePreventive: true,
            maxTreatmentsPerProblem: 2,
            minConfidence: payload.threshold || 0.3,
            prioritizeByConfidence: true
          },
          storeContext // Pass store context for database access
        );
        
        // Store the generated treatment plan
        commit('SET_SUGGESTED_TREATMENTS', treatmentPlan);
        commit('SET_AUTO_GENERATED_PLAN', treatmentPlan);
        commit('SET_TREATMENT_MAPPINGS', {
          detectionKeywords: TreatmentMapping.getDetectionKeywords(),
          fallbackTreatments: TreatmentMapping.getFallbackTreatments(),
          databaseIntegrated: true,
          timestamp: new Date().toISOString()
        });
        
        // Create enhanced legacy format for backward compatibility
        const legacyResults = {
          problems: convertToLegacyFormat(results.detections),
          recommendations: treatmentPlan.map(t => ({
            treatment: t.detail,
            diagnosis: t.diagnosisName,
            code: t.treatmentCode,
            price: t.price,
            priority: t.priority,
            confidence: t.aiConfidence,
            databaseBased: true  // All are from database now, no fallbacks
          })),
          overallOralHealth: summary?.overallOralHealth || 'good',
          urgentCare: summary?.highConfidenceCount > 3,
          totalProblems: summary?.totalProblems || 0,
          detectionCategories: summary?.categoryCount || {},
          treatmentPlanStats: {
            totalTreatments: treatmentPlan.length,
            databaseTreatments: treatmentPlan.length,  // All from database now
            fallbackTreatments: 0,  // No fallbacks
            averageConfidence: treatmentPlan.length > 0 ?
              treatmentPlan.reduce((sum, t) => sum + t.aiConfidence, 0) / treatmentPlan.length : 0
          },
          isFallback: false,  // Never fallback
          databaseIntegrated: true,
          analysisTimestamp: new Date().toISOString()
        };
        
        commit('SET_ANALYSIS_RESULTS', legacyResults);
        
        return {
          treatmentPlan,
          summary,
          legacyResults,
          databaseStats: {
            diagnosisLookups: treatmentPlan.filter(t => t.diagnosis > 0).length,
            treatmentLookups: treatmentPlan.filter(t => t.treatment > 0).length,
            fallbacks: 0  // No fallbacks anymore
          }
        };
        
      } catch (error) {
        console.error('Failed to process analysis results:', error);
        // No fallback - just throw the error
        throw error;
      }
    },
    
    // Removed generateFallbackResults - no fallback when AI fails
    
    // Validate database connectivity for treatment mapping
    validateDatabaseAccess({ rootState }) {
      try {
        const treatDiagList = rootState.moduleAppointment?.treatDiagList || [];
        const treatList = rootState.moduleAppointment?.treatList || [];
        
        const isValid = treatDiagList.length > 0 && treatList.length > 0;
        
        return isValid;
      } catch (error) {
        console.error('Database access validation failed:', error);
        return false;
      }
    },
    
    // Get database statistics for debugging
    getDatabaseStats({ rootState }) {
      try {
        const treatDiagList = rootState.moduleAppointment?.treatDiagList || [];
        const treatList = rootState.moduleAppointment?.treatList || [];
        const nhsoTreatments = treatList.filter(t => t.branchId === 0);
        
        return {
          totalDiagnoses: treatDiagList.length,
          totalTreatments: treatList.length,
          nhsoTreatments: nhsoTreatments.length,
          diagnosisCodes: treatDiagList.map(d => d.code).filter(Boolean).slice(0, 10),
          treatmentCodes: treatList.map(t => t.code).filter(Boolean).slice(0, 10),
          lastUpdated: new Date().toISOString()
        };
      } catch (error) {
        console.error('Failed to get database stats:', error);
        return { error: error.message };
      }
    },
    
    // Move to next step in workflow
    nextStep({ commit, state }) {
      if (state.currentStep < 4) {
        commit('SET_CURRENT_STEP', state.currentStep + 1);
      }
    },
    
    // Move to previous step in workflow
    previousStep({ commit, state }) {
      if (state.currentStep > 1) {
        commit('SET_CURRENT_STEP', state.currentStep - 1);
      }
    },
    
    // Complete workflow and save database-driven treatment plan
    async completeWorkflow({ commit, state, dispatch }, { patientId, clinicUrl, branchUrl }) {
      try {
        // Validate treatment plan before saving
        const validatedPlan = state.autoGeneratedPlan.filter(treatment => {
          const isValid = treatment.diagnosis !== undefined && 
                         treatment.treatment !== undefined &&
                         treatment.price > 0;
          if (!isValid) {
            console.warn('Invalid treatment filtered out:', treatment);
          }
          return isValid;
        });
        
        // TODO: Save the database-driven plan to the treatment plan store
        // This would integrate with existing treatment plan saving logic
        
        commit('SET_WORKFLOW_COMPLETED', true);
        
        // Success message
        const message = `AI treatment plan created: ${validatedPlan.length} treatments`;
        
        eventBus.$emit('alertToast', {
          message,
          variant: 'success'
        });
        
        return {
          treatmentPlan: validatedPlan,
          stats: {
            total: validatedPlan.length,
            databaseBased: validatedPlan.length,  // All from database
            fallback: 0  // No fallbacks
          }
        };
        
      } catch (error) {
        console.error('Failed to complete database-integrated workflow:', error);
        
        eventBus.$emit('alertToast', {
          message: 'Failed to save treatment plan: ' + error.message,
          variant: 'danger'
        });
        
        throw error;
      }
    },
    
    // Reset the entire workflow
    resetWorkflow({ commit }) {
      commit('RESET_WORKFLOW');
    }
  },

  getters: {
    // Check if both photos are captured (maintain backward compatibility)
    hasAllPhotos: (state, getters, rootState) => {
      // Check if we have photos in either the old system or new protocolMedia system
      const hasOldPhotos = state.upperOcclusalPhoto !== null && state.lowerOcclusalPhoto !== null;

      if (rootState.protocolMedia) {
        // Support mixed states: new photos (.file) OR existing photos (.base64)
        const hasNewPhotos = (rootState.protocolMedia.localPhotos?.upper?.file ||
                             rootState.protocolMedia.localPhotos?.upper?.base64) &&
                            (rootState.protocolMedia.localPhotos?.lower?.file ||
                             rootState.protocolMedia.localPhotos?.lower?.base64);
        return hasOldPhotos || hasNewPhotos;
      }

      return hasOldPhotos;
    },
    
    // Get current workflow step
    getCurrentStep: (state) => state.currentStep,
    
    // Check if analysis is complete
    isAnalysisComplete: (state) => {
      return state.analysisResults !== null && !state.analysisInProgress;
    },
    
    // Get formatted treatment plan
    getFormattedTreatmentPlan: (state) => {
      return state.autoGeneratedPlan.map((item, index) => ({
        ...item,
        order: index + 1
      }));
    },
    
    // Check if workflow can proceed to next step (enhanced for database integration)
    canProceedToNextStep: (state) => {
      switch (state.currentStep) {
        case 1: // Photo capture step
          return state.upperOcclusalPhoto && state.lowerOcclusalPhoto;
        case 2: // AI analysis step
          return state.analysisResults !== null;
        case 3: // Treatment plan review
          return state.autoGeneratedPlan.length > 0;
        case 4: // Manual editing
          return true;
        default:
          return false;
      }
    },
    
    // Get treatment plan statistics
    getTreatmentPlanStats: (state) => {
      const plan = state.autoGeneratedPlan;
      if (!plan || plan.length === 0) {
        return {
          total: 0,
          databaseBased: 0,
          fallback: 0,
          averageConfidence: 0,
          averagePrice: 0
        };
      }
      
      const totalPrice = plan.reduce((sum, t) => sum + (t.price || 0), 0);
      const totalConfidence = plan.reduce((sum, t) => sum + (t.aiConfidence || 0), 0);

      return {
        total: plan.length,
        databaseBased: plan.length,  // All from database
        fallback: 0,  // No fallbacks
        averageConfidence: plan.length > 0 ? totalConfidence / plan.length : 0,
        averagePrice: plan.length > 0 ? totalPrice / plan.length : 0,
        totalPrice: totalPrice,
        highConfidenceTreatments: plan.filter(t => t.aiConfidence > 0.8).length,
        urgentTreatments: plan.filter(t => t.priority === 'urgent' || t.priority === 'high').length
      };
    }
  }
};